/**
 * Compact trump indicator floated at the top-right of the game view.
 *
 * Shared across games — pick one of two variants per game:
 *
 *   • `rank` form (Sheng Ji): the trump rank advances each round, so we
 *     show the suit glyph next to the current rank ("♠ 2", "♥ A").
 *   • `direction` form (Regicide): there's no rank, but the rank ladder
 *     can flip mid-round; we show the suit next to ↑ (A-high / reversed)
 *     or ↓ (K-high / normal). Tooltip explains.
 *
 * Either variant fits in a ~80px pill, leaving the table center
 * uncluttered. Previously the trump panel covered nearby UI on phones.
 */

const SUIT_GLYPH: Record<string, string> = { C: "♣", D: "♦", H: "♥", S: "♠" };
const SUIT_IS_RED: Record<string, boolean> = { C: false, D: true, H: true, S: false };
const SUIT_NAME: Record<string, string> = {
  C: "Clubs", D: "Diamonds", H: "Hearts", S: "Spades",
};

export type RankDirection = "up" | "down";

interface CommonProps {
  /**
   * Trump suit letter, or null if no trump. The wider string type lets
   * callers pass game-specific suit unions that include extra members
   * (e.g. Sheng Ji's "J" for jokers) without casts — we only render
   * recognized suits and fall back to "—" otherwise.
   */
  suit: string | null;
  /** Override the bottom label for the suit. Default = full suit name. */
  suitLabel?: string;
  className?: string;
}

interface TrumpBadgeRankProps extends CommonProps {
  /** Current trump rank, e.g. "2", "K", "A". */
  rank: string;
  direction?: undefined;
  rankLetter?: undefined;
}

interface TrumpBadgeDirectionProps extends CommonProps {
  rank?: undefined;
  /**
   * "up" = highest card is at the bottom of the natural deck order
   *        (e.g. Regicide reversed mode: A beats K).
   * "down" = highest card is at the top of the natural order (Regicide
   *        normal mode: K beats A).
   */
  direction: RankDirection;
  /** Letter shown next to the arrow, e.g. "A" or "K". */
  rankLetter: string;
}

/**
 * Suit-only variant — neither rank nor direction. Used by games like
 * Euchre where the only state we surface is the trump suit (plus a
 * "Alone" / "Maker" label via `suitLabel`).
 */
interface TrumpBadgeSuitOnlyProps extends CommonProps {
  rank?: undefined;
  direction?: undefined;
  rankLetter?: undefined;
}

export type TrumpBadgeProps =
  | TrumpBadgeRankProps
  | TrumpBadgeDirectionProps
  | TrumpBadgeSuitOnlyProps;

export function TrumpBadge(props: TrumpBadgeProps) {
  const { suit, suitLabel, className } = props;
  const known = suit != null && suit in SUIT_GLYPH;
  const red = known && SUIT_IS_RED[suit!] === true;
  const glyph = known ? SUIT_GLYPH[suit!] : "—";
  const fullName = known ? SUIT_NAME[suit!] ?? "" : suit == null ? "No trump" : "";

  let mainText: string | null = null;
  let arrow: string | null = null;
  let title: string;
  if (props.rank !== undefined) {
    mainText = props.rank;
    title = `Trump: ${fullName}, rank ${props.rank}`;
  } else if (props.direction !== undefined) {
    arrow = props.direction === "up" ? "↑" : "↓";
    mainText = props.rankLetter;
    title =
      props.direction === "up"
        ? `Trump: ${fullName}. Rank reversed — ${props.rankLetter} is highest.`
        : `Trump: ${fullName}. Rank normal — ${props.rankLetter} is highest.`;
  } else {
    // Suit-only variant — just the suit glyph + label.
    title = `Trump: ${fullName}`;
  }

  return (
    <div className={`trump-badge${className ? " " + className : ""}`} title={title}>
      <span className={`trump-badge-suit${red ? " red" : ""}`} aria-hidden="true">
        {glyph}
      </span>
      {mainText !== null && (
        <span className="trump-badge-rank">
          {arrow && <span className="trump-badge-arrow">{arrow}</span>}
          {mainText}
        </span>
      )}
      <span className="trump-badge-label">{suitLabel ?? fullName}</span>
    </div>
  );
}
